import { atom } from 'jotai';
import intersection from 'lodash/intersection';
import type { GenericAnnotationT, LabellingValidationsT, RangeSpanT } from '../../../common/types/types';
import { colorWithOpacity } from '../../../common/utils/color-with-opacity';
import { getLightShadeForStrokeColor } from '../../../common/utils/utils';
import { createSidePanelAtoms } from '../../../modernization-common/side-panel/atoms/expansion-atoms';
import { questionnaireDetailsMapAtom } from '../../../modernization-common/side-panel/atoms/questionnaire.atom';
import { TASK_QUESTIONNAIRE_ID } from '../../../modernization-common/side-panel/const';
import type { RemainingItem } from '../../../modernization-common/side-panel-m4/remaining-items/v2/types';
import { ITEM_CATEGORY, OBJECT_TYPE, REMAINING_ITEMS_GROUPING_KEY, TAG_LABEL } from '../../../modernization-common/side-panel-m4/remaining-items/v2/types';
import { REMAINING_ITEM_OBJECT } from '../../../modernization-common/side-panel-m4/types';
import type { AnnotationStoreT } from '../../labelling/types';
import type { SidePanelImageAnnotation } from '../components/side-panel/side-panel';
import { getTagsForAnnotation, mapAnnotationToSidePanelAnnotation } from '../components/side-panel/utils';
import { VIDEO_FILTERS } from '../constants';
import type { MetadataPayload } from '../studio-store/types';
import { buildAnnotationLabel, buildSidePanelAnnotation, DEFAULT_ANNOTATION_LABEL_STYLE } from '../utils/build-side-panel-annotation';
import { getAnnotationQuestionnairePendingStatus, isAnnotationQuestionnairePending } from '../utils/questionnaire-pending-validations';
import { annotationIdToLabelMapAtom, annotationMetadataAtom, annotationUIStateAtom, currentFrameLoadedAnnotationsAtom, enhancedMLAnnotationFramesWithUIStateAtom, frameCountAtom } from './annotation-atoms';
import { frameRateAtom } from './file-atoms';
import { imageQuadrantValidationRemainingItemsAtom, QUADRANT_IMBALANCE_REMAINING_ITEM_ID } from './image-quadrant-validation-atoms';
import { labelConstraintRemainingItemsAtom } from './label-constraint-validation-atoms';
import { isVideoLabelingM4EnabledAtom, taskQuestionnaireStatusAtom } from './m4-atoms';
import { qaEngineRemainingItemAtom } from './qa-engine-atom';
import { timeDisplayModeAtom } from './tool-atoms';
import { getRemainingItemsMessage } from './utils';
export const {
  expandedAnnotationIdsAtom,
  expandedAnnotationSectionIdsAtom,
  visibilityModeAtom,
  sidePanelAtomDispatch
} = createSidePanelAtoms();
export const annotationIdToSpanAtom = atom<{
  [key: string]: RangeSpanT[];
}>(get => {
  const metadata = get(annotationMetadataAtom);
  return Object.fromEntries([...metadata.entries()].map(([id, m]) => [id, m.spans]));
});
export const filteredAnnotationsAtom = atom<SidePanelImageAnnotation[]>(get => {
  const metadata = get(annotationMetadataAtom);
  const annotationIdToLabelMap = get(annotationIdToLabelMapAtom);
  const annotationUIState = get(annotationUIStateAtom);
  const selectedFilter = get(selectedFilterAtom);
  const videoFilter = get(videoFilterAtom);
  const timeDisplayMode = get(timeDisplayModeAtom);
  const frameRate = get(frameRateAtom);
  const maxFrames = get(frameCountAtom);
  const isVideoTask = maxFrames > 1;
  const passesFilter = (labelId: string) => !selectedFilter || labelId === selectedFilter;
  const buildAnnotation = (annotationId: string, m: MetadataPayload, includeSpan: boolean) => {
    const annotationLabel = annotationIdToLabelMap.get(annotationId) ?? {
      title: m.labelId,
      style: DEFAULT_ANNOTATION_LABEL_STYLE
    };
    const uiState = annotationUIState.get(annotationId);
    return buildSidePanelAnnotation({
      annotationId,
      metadata: m,
      annotationLabel,
      hidden: uiState?.hidden ?? false,
      locked: uiState?.locked ?? false,
      annotationSpan: includeSpan ? m.spans : undefined,
      timeDisplayMode,
      frameRate,
      maxFrames
    });
  };
  switch (videoFilter) {
    case VIDEO_FILTERS.CURRENT_FRAME:
      {
        const currentFrameAnnotations = get(currentFrameLoadedAnnotationsAtom);
        const result: SidePanelImageAnnotation[] = [];
        for (const annotationId of currentFrameAnnotations.keys()) {
          const m = metadata.get(annotationId);
          if (!m || !passesFilter(m.labelId)) {
            continue;
          }
          result.push(buildAnnotation(annotationId, m, isVideoTask));
        }
        return result;
      }
    case VIDEO_FILTERS.ALL:
      {
        const result: SidePanelImageAnnotation[] = [];
        for (const [annotationId, m] of metadata) {
          if (!passesFilter(m.labelId)) {
            continue;
          }
          result.push(buildAnnotation(annotationId, m, isVideoTask));
        }
        return result;
      }
    default:
      {
        (videoFilter satisfies never);
        return [];
      }
  }
});
export const annotationsAtom = atom<SidePanelImageAnnotation[]>(get => {
  const metadata = get(annotationMetadataAtom);
  const annotationIdToLabelMap = get(annotationIdToLabelMapAtom);
  const annotationUIState = get(annotationUIStateAtom);
  const timeDisplayMode = get(timeDisplayModeAtom);
  const frameRate = get(frameRateAtom);
  const maxFrames = get(frameCountAtom);
  return [...metadata.entries()].map(([annotationId, m]) => {
    const annotationLabel = buildAnnotationLabel(m, annotationIdToLabelMap);
    const uiState = annotationUIState.get(annotationId);
    return buildSidePanelAnnotation({
      annotationId,
      metadata: m,
      annotationLabel,
      hidden: uiState?.hidden ?? false,
      locked: uiState?.locked ?? false,
      annotationSpan: undefined,
      timeDisplayMode,
      frameRate,
      maxFrames
    });
  });
});
export const mlAnnotationsAtom = atom<SidePanelImageAnnotation[]>(get => {
  const mlAnnotationFrames = get(enhancedMLAnnotationFramesWithUIStateAtom);
  const timeDisplayMode = get(timeDisplayModeAtom);
  const frameRate = get(frameRateAtom);
  const maxFrames = get(frameCountAtom);
  const allMLAnnotationMap: AnnotationStoreT = {};
  mlAnnotationFrames.forEach(frameAnnotations => {
    Object.assign(allMLAnnotationMap, frameAnnotations);
  });
  return Object.entries(allMLAnnotationMap).map(([id, annotation]) => mapAnnotationToSidePanelAnnotation({
    id,
    annotation,
    timeDisplayMode,
    frameRate,
    maxFrames
  }));
});
export const importedMLAnnotationIdsAtom = atom<string[]>(get => {
  const annotationMetadata = get(annotationMetadataAtom);
  const mlAnnotationFrames = get(enhancedMLAnnotationFramesWithUIStateAtom);
  const allAnnotationOriginalIdsSet = new Set<string>();
  for (const meta of annotationMetadata.values()) {
    if (meta.originalId) {
      allAnnotationOriginalIdsSet.add(meta.originalId);
    }
  }
  const allMLAnnotationsOriginalIdsSet = new Set<string>();
  mlAnnotationFrames.forEach(frameAnnotations => {
    Object.values(frameAnnotations).forEach(annotation => {
      if (annotation.metadata?.originalId) {
        allMLAnnotationsOriginalIdsSet.add(annotation.metadata.originalId);
      }
    });
  });
  const allAnnotationOriginalIds = Array.from(allAnnotationOriginalIdsSet);
  const allMLAnnotationsOriginalIds = Array.from(allMLAnnotationsOriginalIdsSet);
  return intersection(allAnnotationOriginalIds, allMLAnnotationsOriginalIds);
});
export const selectedFilterAtom = atom<string>();
export const videoFilterAtom = atom<(typeof VIDEO_FILTERS)[keyof typeof VIDEO_FILTERS]>(VIDEO_FILTERS.ALL);

// annotationsMap which holds the frame span for each annotation
export const annotationFrameSpanAtom = atom<{
  [key: string]: RangeSpanT;
}>(get => {
  const metadata = get(annotationMetadataAtom);
  const result: {
    [key: string]: RangeSpanT;
  } = {};
  for (const [id, m] of metadata) {
    if (m.spans.length > 0) {
      const firstSpan = m.spans[0];
      const lastSpan = m.spans[m.spans.length - 1];
      if (firstSpan && lastSpan) {
        result[id] = {
          start: firstSpan.start,
          end: lastSpan.end
        };
      }
    }
  }
  return result;
});
export const allAnnotationMapAtom = atom<{
  [key: string]: {
    annotation: GenericAnnotationT;
    startFrameIndex: number;
    endFrameIndex: number;
  };
}>(get => {
  const metadata = get(annotationMetadataAtom);
  const annotationIdToLabelMap = get(annotationIdToLabelMapAtom);
  const annotationUIState = get(annotationUIStateAtom);
  const result: {
    [key: string]: {
      annotation: GenericAnnotationT;
      startFrameIndex: number;
      endFrameIndex: number;
    };
  } = {};
  for (const [id, m] of metadata) {
    if (m.spans.length === 0) {
      continue;
    }
    const firstSpan = m.spans[0];
    const lastSpan = m.spans[m.spans.length - 1];
    if (!firstSpan || !lastSpan) {
      continue;
    }
    const annotationLabel = annotationIdToLabelMap.get(id) ?? {
      title: m.labelId,
      style: DEFAULT_ANNOTATION_LABEL_STYLE
    };
    const uiState = annotationUIState.get(id);
    result[id] = {
      annotation: {
        id,
        annotationType: m.annotationType,
        annotationLabel,
        hidden: uiState?.hidden ?? false,
        locked: uiState?.locked ?? false,
        metadata: {
          answers: m.answers,
          consensusInfo: m.consensusInfo
        }
      },
      startFrameIndex: firstSpan.start,
      endFrameIndex: lastSpan.end
    };
  }
  return result;
});
const getAnnotationsDataForPendingItemsAtom = atom(get => {
  const annotations = get(annotationsAtom);
  const questionnaireCache = get(questionnaireDetailsMapAtom);
  const annotationIdToSpan = get(annotationIdToSpanAtom);
  return {
    annotations,
    questionnaireCache,
    annotationIdToSpan
  };
});
export const pendingAnnotationAtom = atom<SidePanelImageAnnotation[]>(get => {
  const {
    annotations,
    questionnaireCache,
    annotationIdToSpan
  } = get(getAnnotationsDataForPendingItemsAtom);
  const timeDisplayMode = get(timeDisplayModeAtom);
  const frameRate = get(frameRateAtom);
  const maxFrames = get(frameCountAtom);
  const metadata = get(annotationMetadataAtom);
  const pendingAnnotations = annotations.filter(annotation => {
    const annotationSpan = annotationIdToSpan[annotation.id];
    if (!annotationSpan || annotationSpan.length === 0) {
      console.error('annotation span is empty, this should not be possible');
      return false;
    }
    const m = metadata.get(annotation.id);
    const annotationForCheck = {
      annotationLabel: annotation.annotationLabel,
      answers: m?.answers,
      dynamicAnswers: m?.dynamicAnswers
    };
    return isAnnotationQuestionnairePending(annotationForCheck, questionnaireCache);
  });
  if (!pendingAnnotations.length) {
    return [];
  }
  const isVideoTask = maxFrames > 1;
  return pendingAnnotations.map(annotation => {
    if (isVideoTask) {
      const annotationSpan = annotationIdToSpan[annotation.id] || [];
      return {
        ...annotation,
        tags: getTagsForAnnotation({
          annotation,
          annotationSpan,
          timeDisplayMode,
          frameRate,
          maxFrames
        })
      };
    }
    return annotation;
  });
});
export const pendingAnnotationCountAtom = atom<number>(get => {
  const pendingAnnotations = get(pendingAnnotationAtom);
  return pendingAnnotations.length;
});
export const staticAndDynamicQuestionnairePendingAtom = atom(get => {
  const {
    annotations,
    questionnaireCache,
    annotationIdToSpan
  } = get(getAnnotationsDataForPendingItemsAtom);
  const metadata = get(annotationMetadataAtom);
  return annotations.filter(annotation => {
    const annotationSpan = annotationIdToSpan[annotation.id];
    return annotationSpan && annotationSpan.length > 0;
  }).reduce((acc, curr) => {
    const m = metadata.get(curr.id);
    const annotationForCheck = {
      annotationLabel: curr.annotationLabel,
      answers: m?.answers,
      dynamicAnswers: m?.dynamicAnswers
    };
    return {
      ...acc,
      [curr.id]: getAnnotationQuestionnairePendingStatus(annotationForCheck, questionnaireCache)
    };
  }, ({} as {
    [id: string]: {
      dynamicQuestionnairePending: boolean;
      staticQuestionnairePending: boolean;
    };
  }));
});
export const hideNoEntityCheckboxAtom = atom<boolean>(false);
export const validationsAtom = atom<LabellingValidationsT>({
  invalidSource: false,
  labelValidation: {
    isRequiredLabelAnnotated: true,
    reasons: []
  }
});
export const remainingItemsV2Atom = atom<RemainingItem[]>(get => {
  const isM4Enabled = get(isVideoLabelingM4EnabledAtom);
  const taskQuestionnaireStatus = get(taskQuestionnaireStatusAtom);
  const qaEngineItems = get(qaEngineRemainingItemAtom);
  const quadrantValidationItems = get(imageQuadrantValidationRemainingItemsAtom);
  const labelConstraintItems = get(labelConstraintRemainingItemsAtom);
  const pendingStatusMap = get(staticAndDynamicQuestionnairePendingAtom);
  const {
    annotations
  } = get(getAnnotationsDataForPendingItemsAtom);
  const pendingAnnotations = get(pendingAnnotationAtom);
  const annotationIdToLabelMap = get(annotationIdToLabelMapAtom);
  const taskQuestionnaireItem: RemainingItem[] = isM4Enabled && taskQuestionnaireStatus === 'PENDING' ? [{
    id: TASK_QUESTIONNAIRE_ID,
    label: 'Task',
    objectType: OBJECT_TYPE.TASK_QUESTIONNAIRE,
    category: ITEM_CATEGORY.PENDING,
    description: 'Incomplete task questionnaire',
    tagLabel: TAG_LABEL.QUESTIONNAIRE,
    groupingKey: REMAINING_ITEMS_GROUPING_KEY.QUESTIONNAIRE
  }] : [];
  const pendingAnnotationItems: RemainingItem[] = annotations.flatMap(annotation => {
    const pendingStatus = pendingStatusMap[annotation.id];
    const pendingAnnotation = pendingAnnotations.find(pending => pending.id === annotation.id);
    if (!pendingStatus || !pendingAnnotation) {
      return [];
    }
    const {
      strokeColor
    } = annotation.annotationLabel.style;
    const lighterShade = getLightShadeForStrokeColor(strokeColor);
    const pendingMessage = getRemainingItemsMessage(pendingStatus);
    return [{
      id: annotation.id,
      label: `${annotation.annotationLabel.title} [${annotation.id}]`,
      objectType: OBJECT_TYPE.ANNOTATION,
      category: ITEM_CATEGORY.PENDING,
      description: pendingMessage,
      tagLabel: TAG_LABEL.QUESTIONNAIRE,
      groupingKey: REMAINING_ITEMS_GROUPING_KEY.QUESTIONNAIRE,
      tagColor: {
        primary: strokeColor,
        secondary: colorWithOpacity(strokeColor, 0.2),
        ...(lighterShade && {
          tertiary: lighterShade
        })
      }
    }];
  });
  const ruleEngineItems: RemainingItem[] = qaEngineItems.map(qaItem => {
    const annotationLabel = qaItem.objectType === REMAINING_ITEM_OBJECT.ANNOTATION ? annotationIdToLabelMap.get(qaItem.id) : undefined;
    const strokeColor = annotationLabel?.style.strokeColor;
    const lighterShade = strokeColor ? getLightShadeForStrokeColor(strokeColor) : undefined;
    return {
      id: qaItem.id,
      label: qaItem.label,
      objectType: qaItem.objectType === REMAINING_ITEM_OBJECT.ANNOTATION ? OBJECT_TYPE.ANNOTATION : OBJECT_TYPE.TASK,
      category: ITEM_CATEGORY.ERROR,
      description: qaItem.message,
      tagLabel: TAG_LABEL.RULE_ENGINE,
      groupingKey: REMAINING_ITEMS_GROUPING_KEY.RULE_ENGINE,
      customActions: qaItem.customActions,
      ...(strokeColor && {
        tagColor: {
          primary: strokeColor,
          secondary: colorWithOpacity(strokeColor, 0.2),
          ...(lighterShade && {
            tertiary: lighterShade
          })
        }
      })
    };
  });
  const quadrantItems: RemainingItem[] = quadrantValidationItems.map(quadrantItem => {
    const isQuadrantImbalance = quadrantItem.id === QUADRANT_IMBALANCE_REMAINING_ITEM_ID;
    return {
      id: quadrantItem.id,
      label: quadrantItem.label,
      objectType: OBJECT_TYPE.TASK,
      category: ITEM_CATEGORY.ERROR,
      description: quadrantItem.message,
      tagLabel: isQuadrantImbalance ? TAG_LABEL.QUADRANT_IMBALANCE : TAG_LABEL.MINIMUM_ANNOTATIONS,
      groupingKey: isQuadrantImbalance ? REMAINING_ITEMS_GROUPING_KEY.QUADRANT_IMBALANCE : REMAINING_ITEMS_GROUPING_KEY.MINIMUM_ANNOTATIONS
    };
  });
  return [...taskQuestionnaireItem, ...pendingAnnotationItems, ...ruleEngineItems, ...quadrantItems, ...labelConstraintItems];
});
export const remainingItemsCountAtom = atom<number>(get => {
  const remainingItems = get(remainingItemsV2Atom);
  return remainingItems.length;
});